import React, { useEffect, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import AuthLoading from './AuthLoading';
import { authService } from '../services/authService';
import { sanitizeReturnTo } from '../utils/returnTo';

const LoginCallback = () => {
  const { oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const handledRef = useRef(false);

  useEffect(() => {
    // The authorization code in the callback URL can only be parsed once.
    // React StrictMode (dev) double-invokes this effect, so a second
    // handleLoginRedirect() throws "Unable to parse a token from the url".
    // The old catch handler responded with `window.location.href = '/'`, a
    // full-page reload that re-bootstrapped the SPA *after* the app had
    // already rendered — that reload is the post-login "Signing you in…"
    // flash users reported. Guard so the redirect is handled exactly once and
    // never falls back to a hard reload.
    if (handledRef.current) return;
    handledRef.current = true;

    const handleCallback = async () => {
      const cleanupFailedCallback = () => {
        try { sessionStorage.removeItem('lyric.returnTo'); } catch { /* ignore */ }
        try { sessionStorage.removeItem('lyric.authSettling'); } catch { /* ignore */ }
      };

      try {
        console.log('Starting login callback handling...');
        console.log('Current URL:', window.location.href);

        // Mark that we're in the post-login settling window. SecureRoute reads
        // this to keep the loader up (instead of flashing <LoginPage />) during
        // the brief moment after the callback where authState still reads
        // `isAuthenticated: false` before the freshly-stored tokens propagate.
        try { sessionStorage.setItem('lyric.authSettling', String(Date.now())); } catch { /* ignore */ }

        // Clear the logout latch immediately. If a stale value from an earlier
        // sign-out is still in sessionStorage when SecureRoute mounts after
        // this callback, it will wipe the tokens we're about to acquire and
        // force the user through a second login. The latch only ever needs to
        // survive the brief /logout bounce — it has no business here.
        try { sessionStorage.removeItem('lyric.justLoggedOut'); } catch { /* ignore */ }

        // Only parse the redirect when the code is actually present. If it was
        // already consumed (e.g. a duplicate invocation that slipped past the
        // ref guard), there is nothing to parse — soft-navigate home instead
        // of calling handleLoginRedirect() and throwing.
        if (typeof oktaAuth.isLoginRedirect === 'function' && !oktaAuth.isLoginRedirect()) {
          console.log('No login redirect to parse; navigating home.');
          cleanupFailedCallback();
          navigate('/', { replace: true });
          return;
        }

        // handleLoginRedirect() invokes our restoreOriginalUri (see
        // SecurityShell in App.js), which does a soft react-router navigate —
        // no full-page reload, no flash.
        await oktaAuth.handleLoginRedirect();
        
        console.log('Login callback completed successfully');
        
        // Verify we have tokens after callback
        const tokenManager = oktaAuth.tokenManager;
        const accessToken = await tokenManager.get('accessToken');
        const idToken = await tokenManager.get('idToken');
        
        console.log('Tokens after callback:', {
          hasAccessToken: !!accessToken,
          hasIdToken: !!idToken
        });

        // ── Deployed-app return path (single-pass) ────────────────────────
        // A logged-out user who opened /apps/<name>/… was bounced here by the
        // proxy with ?return_to=…, which LoginPage stashed in sessionStorage
        // before the Okta redirect. Historically we waited for SecureRoute's
        // authState-driven effect to establish the server session and redirect
        // to the app — but okta-react re-emits a transient
        // `isAuthenticated: false` right after this callback and, on React 19,
        // does not reliably propagate the authenticated authState on the FIRST
        // callback. That left the user stranded on <LoginPage /> and forced a
        // SECOND sign-in (the "log in twice" bug on deployed apps).
        //
        // Fix: do it HERE, where we already hold freshly-minted tokens and
        // don't depend on authState settling. Establish the session cookie
        // (regenerated + saved server-side before the response returns), then
        // hard-navigate to the app in a single pass. Because the cookie is set
        // before we leave, the /apps/* proxy gate passes instead of bouncing
        // the user back to login.
        let pendingReturnTo = null;
        try { pendingReturnTo = sanitizeReturnTo(sessionStorage.getItem('lyric.returnTo')); } catch { /* ignore */ }
        if (pendingReturnTo) {
          try {
            await authService.establishSession();
            try { sessionStorage.removeItem('lyric.returnTo'); } catch { /* ignore */ }
            try { sessionStorage.removeItem('lyric.authSettling'); } catch { /* ignore */ }
            console.log(`Session established after callback; redirecting to ${pendingReturnTo}`);
            window.location.replace(pendingReturnTo);
            return; // navigating away — stop here
          } catch (sessErr) {
            // Leave lyric.returnTo in place so SecureRoute can retry once
            // authState settles. Fall through to the normal soft-navigate flow.
            console.warn('Post-callback establishSession failed; deferring return_to to SecureRoute:', sessErr.message);
          }
        }
        
      } catch (error) {
        console.error('Login callback error:', error);
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          errorCode: error.errorCode,
          errorSummary: error.errorSummary
        });

        // Handle specific token refresh errors by clearing the stale tokens.
        if (error.message && error.message.includes('refresh')) {
          console.error('Token refresh error during callback - clearing tokens...');
          try {
            await oktaAuth.tokenManager.clear();
          } catch (clearError) {
            console.error('Failed to clear tokens:', clearError);
          }
        }

        // If the callback never completed, discard any stashed deployed-app
        // destination so a soft navigate back home shows the normal login path
        // instead of re-triggering the auto-redirect loop on the next mount.
        cleanupFailedCallback();

        // Recover with a soft react-router navigate — never a hard
        // window.location reload, which is what caused the post-login flash.
        navigate('/', { replace: true });
      }
    };

    handleCallback();
  }, [oktaAuth, navigate]);

  return <AuthLoading message="Signing you in…" />;
};

export default LoginCallback;
